/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { login } from '../core/_requests'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'
import { LanguageHelp, setLanguage } from '../../../../_metronic/helpers/LanguageHelper'
import { toast } from 'react-toastify';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email(LanguageHelp('Wrong email format'))
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(100, LanguageHelp('Maximum 100 symbols'))
    .required(LanguageHelp('Email is required')),
  password: Yup.string()
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(50, LanguageHelp('Maximum 50 symbols'))
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,15})/, LanguageHelp("Your password should be 8-15 characters long and should contain at least one lowercase, one uppercase, one numeric digit and one special characters!"))
    .required(LanguageHelp('Password is required')),
  isRememberMeChecked: Yup.bool()
})

const initialValues = {
  email: '',
  password: '',
  isRememberMeChecked: false,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const { saveAuth } = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      // alert(localStorage.getItem('aisenseus_di') as string)
      setLoading(true)
      try {
        const { data: auth } = await login(values.email, values.password, values.isRememberMeChecked, localStorage.getItem('aisenseus_di') as string)

        if (auth.httpStatusCode === 400) {
          toast.error(LanguageHelp('The login details are incorrect'));
          console.error(auth.message)
          saveAuth(undefined)
          setStatus('The login details are incorrect')
          setSubmitting(false)
          setLoading(false)
        }
        else { saveAuth(auth); setLanguage(auth.result.language, false) }

      } catch (error) {
        toast.error(LanguageHelp('The login details are incorrect'));
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <div className="text-center mb-10"><h1 className="text-dark fw-bolder mb-3">{LanguageHelp('Login')}</h1></div>
        <label className='form-label fs-6 fw-bolder text-dark mb-1'>{LanguageHelp('Email')}</label>
        <input
          placeholder={LanguageHelp('Email')}
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>{LanguageHelp('Password')}</label>
        <input
          placeholder={LanguageHelp('Password')}
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div>
          <label className='form-check form-check-inline mt-5' htmlFor='kt_login_toc_agree'>
            <input
              className='form-check-input'
              type='checkbox'
              id='kt_login_toc_agree'
              {...formik.getFieldProps('isRememberMeChecked')}
            />
            <span>
              {LanguageHelp('Remember Me')}
            </span>
          </label>
        </div>

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary mt-5'>
          {LanguageHelp('Forgot Password ?')}
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>{LanguageHelp('Login')}</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {LanguageHelp('Please Wait...')}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        {LanguageHelp('Not a Member yet?')}{' '}
        <Link to='/auth/registration' className='link-primary'>
          {LanguageHelp('Sign up')}
        </Link>
      </div>
    </form>
  )
}